import { call } from '@advanza/api'
import LoadingDots from '@trustoo/components/ui/LoadingDots'
import { GuaranteedStatus } from '@trustoo/types/api/controllers/guaranteed'
import CheckButton from 'components/CheckButton'
import Restricted from 'components/Restricted'
import { format, parseISO } from 'date-fns'
import { useCallback, useEffect, useState } from 'react'

interface Props {
    providerId: number
}

/** The criteria the backend reports, spelled out for CS. */
const CRITERION_LABELS: Record<string, string> = {
    months_in_business: 'Less than 24 months in business',
    months_signed: 'Less than 3 months premium',
    no_overdue_invoices_block: 'Blocked for overdue invoices',
    avg_rating: 'Review score below 4.5',
    nr_ratings: 'Fewer than 20 reviews',
    low_rating_share: '10% or more 1 and 2 star reviews',
    country: 'Not a Dutch provider',
}

const DATE_FORMAT = 'd MMM yyyy'

const ProviderGuaranteed = ({ providerId }: Props) => {
    const [status, setStatus] = useState<GuaranteedStatus | undefined>()
    const [isSaving, setIsSaving] = useState(false)

    const fetchStatus = useCallback(
        () =>
            call(`office/guaranteed/get-status/${providerId}`).then((response) =>
                setStatus(response as GuaranteedStatus),
            ),
        [providerId],
    )

    useEffect(() => {
        fetchStatus()
    }, [fetchStatus])

    if (!status) {
        return <LoadingDots />
    }

    const onToggle = () => {
        setIsSaving(true)
        call(`office/guaranteed/save-enabled/${providerId}`, {
            method: 'POST',
            json: { enabled: !status.isEnabled },
        })
            .then(fetchStatus)
            .finally(() => setIsSaving(false))
    }

    // The pro can only be switched on while they qualify, so a blocked toggle needs
    // to say what is missing instead of just refusing.
    const missingCriteria = status.failedCriteria.map(
        (criterion) => CRITERION_LABELS[criterion] ?? criterion,
    )
    const hasGuaranteedService = status.services.some(({ charge }) => charge !== null)

    return (
        <div className="flex flex-col gap-3">
            <Restricted location="providers" checkRead={false}>
                <div className="flex items-center gap-2">
                    <CheckButton
                        msg="Trustoo Guaranteed enabled"
                        checked={status.isEnabled}
                        disabled={isSaving || (!status.isEligible && !status.isEnabled)}
                        onChange={onToggle}
                    />
                    {isSaving && <LoadingDots />}
                </div>
            </Restricted>
            {!status.isEligible && (
                <div className="text-sm text-grey-800">
                    Not eligible
                    {missingCriteria.length > 0 && `: ${missingCriteria.join(', ')}`}
                    {missingCriteria.length === 0 &&
                        !hasGuaranteedService &&
                        ': no active subscription in a service with Guaranteed enabled'}
                </div>
            )}
            <div className="flex flex-col gap-1">
                {status.services.map(({ serviceId, name, charge }) => (
                    <div key={serviceId} className="flex gap-2 text-sm">
                        <span>{name}</span>
                        <span className={charge === null ? 'text-grey-800' : 'text-green-800'}>
                            {charge === null ? 'not available' : `+ € ${charge.toFixed(2)}`}
                        </span>
                    </div>
                ))}
            </div>
            {status.freePeriods.length > 0 && (
                <div className="flex flex-col gap-1">
                    {status.freePeriods.map(({ dateStart, dateEnd, endedEarly }) => (
                        <div key={dateStart} className="flex gap-2 text-sm">
                            <span>
                                Free month {format(parseISO(dateStart), DATE_FORMAT)} –{' '}
                                {format(parseISO(dateEnd), DATE_FORMAT)}
                            </span>
                            {endedEarly !== null && (
                                <span className="text-neutral-600">
                                    (ended early {format(parseISO(endedEarly), DATE_FORMAT)})
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

export default ProviderGuaranteed
